/**
 * VitaBrace premium cart confirmation drawer.
 *
 * Trigger: Horizon dispatches `shopify:cart:lines-update` with action 'add'. That event does
 * not carry the product title, so we also observe /cart/add(.js) responses via fetch and
 * XHR to pick up `product_title`. Both paths feed the same debounced open() call, so a
 * single add never shows two pop-ups — and an add fired by an app that skips the theme
 * event still shows one.
 *
 * The pop-up closes itself after a configurable delay (default 15s). Hovering or focusing
 * it pauses the countdown, because a timer that runs out while someone is reading is worse
 * than no timer at all.
 */
class MbCartPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.titleEl = this.querySelector('[data-mb-product-title]');
    this.imageEl = this.querySelector('[data-mb-product-image]');
    this.imageWrap = this.querySelector('[data-mb-image-wrap]');
    this.progressEl = this.querySelector('[data-mb-progress]');
    this.cartCountEl = this.querySelector('[data-mb-cart-count]');
    this.shippingBox = this.querySelector('[data-mb-shipping-box]');
    this.shippingMessage = this.querySelector('[data-mb-shipping-message]');
    this.shippingBar = this.querySelector('[data-mb-shipping-bar]');
    this.cartTotalEl = this.querySelector('[data-mb-cart-total]');
    this.freeShippingThreshold = Number(this.dataset.freeShipping || 0);

    this.duration = Number(this.dataset.duration || 15) * 1000;
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.closeDrawer = this.dataset.closeDrawer === 'true';
    this.showImage = this.dataset.showImage === 'true';

    this.remaining = this.duration;
    this.pendingTitle = null;
    this.pendingImage = null;
    this.pendingCount = null;

    this.querySelectorAll('[data-mb-popup-close]').forEach((el) => {
      el.addEventListener('click', () => this.hide());
    });

    // Backdrop click
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.hide();
    });

    if (this.pauseOnHover) {
      this.dialog.addEventListener('mouseenter', () => this.pause());
      this.dialog.addEventListener('mouseleave', () => this.resume());
      this.dialog.addEventListener('focusin', () => this.pause());
      this.dialog.addEventListener('focusout', () => this.resume());
    }

    document.addEventListener('shopify:cart:lines-update', this.#onLinesUpdate);
    this.#interceptRequests();
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this.#onLinesUpdate);
    this.#clearTimer();
  }

  #onLinesUpdate = (event) => {
    const action = event?.action || event?.detail?.action;
    if (action && action !== 'add') return;
    this.#scheduleOpen();
  };

  /**
   * Watches cart-add responses so we can name the product in the message.
   * Never changes the response — it only reads a clone.
   */
  #interceptRequests() {
    const isAddUrl = (url) => typeof url === 'string' && /\/cart\/add(\.js)?(\?|$)/.test(url);

    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch.apply(window, args);
      try {
        const input = args[0];
        const url = typeof input === 'string' ? input : input?.url;
        if (isAddUrl(url) && response.ok) {
          response
            .clone()
            .json()
            .then((data) => this.#capture(data))
            .catch(() => {});
        }
      } catch {
        /* never break the add-to-cart flow over a pop-up */
      }
      return response;
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    const popup = this;
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__mbIsAdd = isAddUrl(url);
      if (this.__mbIsAdd) {
        this.addEventListener('load', () => {
          if (this.status < 200 || this.status >= 300) return;
          try {
            popup.#capture(JSON.parse(this.responseText));
          } catch {
            /* not JSON, ignore */
          }
        });
      }
      return originalOpen.call(this, method, url, ...rest);
    };
  }

  #capture(data) {
    if (!data) return;
    const line = Array.isArray(data.items) ? data.items[0] : data;
    if (line?.product_title) this.pendingTitle = line.product_title;
    if (line?.image || line?.featured_image?.url) {
      this.pendingImage = line.image || line.featured_image.url;
    }
    if (typeof data.item_count === 'number') this.pendingCount = data.item_count;
    this.#scheduleOpen();
  }

  /** Collapses the event and the fetch signal for one add into a single open. */
  #scheduleOpen() {
    clearTimeout(this.openDebounce);
    this.openDebounce = setTimeout(() => this.show(), 120);
  }

  show() {
    if (!this.dialog) return;

    if (this.closeDrawer) this.#dismissCartDrawer();

    if (this.titleEl) {
      this.titleEl.textContent = this.pendingTitle || this.titleEl.dataset.fallback || '';
      this.titleEl.hidden = !this.titleEl.textContent;
    }

    if (this.showImage && this.imageEl && this.imageWrap) {
      if (this.pendingImage) {
        this.imageEl.src = this.pendingImage.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2');
        this.imageWrap.hidden = false;
      } else {
        this.imageWrap.hidden = true;
      }
    }

    if (this.cartCountEl && this.pendingCount != null) {
      const template = this.cartCountEl.dataset.template || '';
      this.cartCountEl.textContent = template.replace('[count]', String(this.pendingCount)).replace('{count}', String(this.pendingCount));
      this.cartCountEl.hidden = !template;
    }

    if (!this.dialog.open) {
      this.dialog.showModal();
      this.dialog.querySelector('[data-mb-autofocus]')?.focus({ preventScroll: true });
    }

    this.#refreshCartSummary();
    this.remaining = this.duration;
    this.#startTimer();
  }

  #formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', { style: 'currency', currency: 'EUR' }).format(value);
    } catch {
      return `€ ${value.toFixed(2).replace('.', ',')}`;
    }
  }

  async #refreshCartSummary() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      this.pendingCount = Number(cart.item_count || 0);
      if (this.cartCountEl) {
        const template = this.cartCountEl.dataset.template || '{count} artikelen';
        this.cartCountEl.textContent = template.replace('[count]', String(this.pendingCount)).replace('{count}', String(this.pendingCount));
        this.cartCountEl.hidden = false;
      }
      if (this.cartTotalEl) this.cartTotalEl.textContent = this.#formatMoney(cart.total_price || 0);

      if (this.shippingBox && this.freeShippingThreshold > 0) {
        const total = Number(cart.total_price || 0);
        const remaining = Math.max(0, this.freeShippingThreshold - total);
        const pct = Math.max(0, Math.min(100, (total / this.freeShippingThreshold) * 100));
        if (this.shippingBar) this.shippingBar.style.width = `${pct}%`;
        if (this.shippingMessage) {
          this.shippingMessage.textContent = remaining <= 0
            ? 'Gratis verzending is behaald ✓'
            : `Nog ${this.#formatMoney(remaining)} tot gratis verzending`;
        }
      }
    } catch {
      /* cart summary is enhancement only */
    }
  }

  hide() {
    this.#clearTimer();
    if (this.dialog?.open) this.dialog.close();
    this.pendingTitle = null;
    this.pendingImage = null;
    this.pendingCount = null;
  }

  #startTimer() {
    this.#clearTimer();
    if (this.duration <= 0) return;

    this.startedAt = Date.now();

    if (this.progressEl) {
      this.progressEl.style.transition = 'none';
      this.progressEl.style.width = '100%';
      // Force a reflow so the transition restarts from 100%.
      void this.progressEl.offsetWidth;
      this.progressEl.style.transition = `width ${this.remaining}ms linear`;
      this.progressEl.style.width = '0%';
    }

    this.timer = setTimeout(() => this.hide(), this.remaining);
  }

  pause() {
    if (!this.timer) return;
    clearTimeout(this.timer);
    this.timer = null;
    this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt));

    if (this.progressEl) {
      const width = getComputedStyle(this.progressEl).width;
      this.progressEl.style.transition = 'none';
      this.progressEl.style.width = width;
    }
  }

  resume() {
    if (this.timer || !this.dialog?.open) return;
    this.#startTimer();
  }

  #clearTimer() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  /** Closes Horizon's cart drawer so the shopper does not get two overlays. */
  #dismissCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component, #cart-drawer');
    if (!drawer) return;

    if (typeof drawer.close === 'function') {
      drawer.close();
      return;
    }

    const openDialog = drawer.matches('dialog[open]') ? drawer : drawer.querySelector('dialog[open]');
    if (openDialog) openDialog.close();
  }
}

if (!customElements.get('mb-cart-popup')) {
  customElements.define('mb-cart-popup', MbCartPopup);
}
